import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Nomination, NominationStatus, AwardType } from '../../types/nomination';
import { isTeamNomination } from '../../types/nominationValidation';
import { NominationServiceWrapper } from '../../services/nominationServiceWrapper';
import {
  approveNomination,
  rejectNomination,
  publishNomination,
} from '../../services/nominationWorkflow';
import {
  canReviewNomination,
  canPublishNomination,
  canCreateNomination,
} from '../../utils/nominationPermissions';
import { useNominationRole } from '../../hooks/useNominationRole';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { notifyNominationSubmitted, notifyNominationApproved, notifyNominationRejected, notifyNominationPublished } from '../../services/inAppNotification';
import { emailNominationSubmitted, emailNominationApproved, emailNominationRejected, emailNominationPublished } from '../../services/emailNotification';
import { getStatusBadgeClass, formatNominationDate, cleanReason } from './NominationCard';
import { getAllUserEmails, getReviewerAndAdminEmails } from '../../services/userLoader';
import InlineLoader from '../common/InlineLoader';
import './NominationDetail.css';

/**
 * Render structured nomination form data from JSON supportingDetails.
 */
function renderFormDetails(nomination: Nomination): React.ReactNode {
  if (!nomination.supportingDetails) return null;
  try {
    const data = JSON.parse(nomination.supportingDetails);
    if (nomination.awardType === AwardType.SPOT) {
      const checkedItems = (obj: Record<string, boolean>) => Object.entries(obj).filter(([, v]) => v).map(([k]) => k.replace(/([A-Z])/g, ' $1').trim());
      return (
        <div className="nom-detail-structured">
          {data.problemType && <div><span className="nomination-detail-label">Problem Type</span><span className="nomination-detail-value">{checkedItems(data.problemType).join(', ') || 'None selected'}</span></div>}
          {data.measurableImpact && <div><span className="nomination-detail-label">Measurable Impact</span><span className="nomination-detail-value">{checkedItems(data.measurableImpact).join(', ') || 'None selected'}</span></div>}
          {data.beyondNormalResponsibility && <div><span className="nomination-detail-label">Beyond Normal Responsibility</span><span className="nomination-detail-value">{data.beyondNormalResponsibility}{data.beyondExplanation ? ` — ${data.beyondExplanation}` : ''}</span></div>}
          {data.clearOwnership && <div><span className="nomination-detail-label">Clear Ownership</span><span className="nomination-detail-value">{data.clearOwnership}{data.ownershipExample ? ` — ${data.ownershipExample}` : ''}</span></div>}
          {data.clientAppreciation && <div><span className="nomination-detail-label">Client Appreciation</span><span className="nomination-detail-value">{data.clientAppreciation}</span></div>}
        </div>
      );
    } else if (nomination.awardType === AwardType.QUARTERLY) {
      const checkedItems = (obj: Record<string, boolean>) => Object.entries(obj).filter(([, v]) => v).map(([k]) => k.replace(/([A-Z])/g, ' $1').trim());
      return (
        <div className="nom-detail-structured">
          {data.scaleOfImpact && <div><span className="nomination-detail-label">Scale of Impact</span><span className="nomination-detail-value">{checkedItems(data.scaleOfImpact).join(', ') || 'None selected'}</span></div>}
          {data.measurableOutcomes && <div><span className="nomination-detail-label">Measurable Outcomes</span><span className="nomination-detail-value">{data.measurableOutcomes}</span></div>}
          {data.leadershipQualities && <div><span className="nomination-detail-label">Leadership Qualities</span><span className="nomination-detail-value">{checkedItems(data.leadershipQualities).join(', ') || 'None selected'}</span></div>}
          {data.sustainableImpact && <div><span className="nomination-detail-label">Sustainable Impact</span><span className="nomination-detail-value">{data.sustainableImpact}{data.sustainableExplanation ? ` — ${data.sustainableExplanation}` : ''}</span></div>}
          {data.standOutReason && <div><span className="nomination-detail-label">Why It Stands Out</span><span className="nomination-detail-value">{data.standOutReason}</span></div>}
        </div>
      );
    }
  } catch {
    // Not JSON — render as plain text
    return <span className="nomination-detail-value">{nomination.supportingDetails}</span>;
  }
  return <span className="nomination-detail-value">{nomination.supportingDetails}</span>;
}

/**
 * NominationDetail Component
 * Displays full nomination details including reviewer notes.
 * Shows action buttons based on user role and nomination status.
 * Requirements: 3.2, 3.5
 */
const NominationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refresh } = useNotifications();

  const [nomination, setNomination] = useState<Nomination | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [noteContent, setNoteContent] = useState('');

  const { nominationRole } = useNominationRole(user);

  useEffect(() => {
    const loadNomination = async () => {
      if (id) {
        const found = await NominationServiceWrapper.getNomination(id);
        setNomination(found || null);
      }
      setLoading(false);
    };
    loadNomination();
  }, [id]);

  if (loading) {
    return <div className="nomination-detail-loading"><InlineLoader message="Loading nomination" /></div>;
  }

  if (!nomination) {
    return (
      <div className="nomination-detail-not-found">
        <h3>Nomination Not Found</h3>
        <p>The nomination you are looking for does not exist.</p>
        <button className="nomination-detail-back-btn" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  const isReviewable =
    nomination.status === NominationStatus.SUBMITTED ||
    nomination.status === NominationStatus.UNDER_REVIEW;

  // Check ownership — nominatorId may store email OR user ID depending on how it was created
  const isOwnNomination = Boolean(
    user?.email && (
      user.email === nomination.nominatorId ||
      user.email === nomination.nominatorName ||
      user.id === nomination.nominatorId
    )
  );
  const isDraft = nomination.status === NominationStatus.DRAFT;

  // DEBUG: Issue 7 — Draft action visibility
  console.log('[NominationDetail][Debug]', {
    nominationStatus: nomination.status,
    isDraft,
    isOwnNomination,
    nominatorId: nomination.nominatorId,
    nominatorName: nomination.nominatorName,
    currentUserEmail: user?.email,
    currentUserId: user?.id,
    nominationRole,
    canCreate: canCreateNomination(nominationRole),
    showDraftActions: isOwnNomination && isDraft && canCreateNomination(nominationRole),
  });

  // Action handlers
  const handleApprove = async () => {
    const result = approveNomination(nomination);
    if (result.success && result.nomination) {
      await NominationServiceWrapper.updateNomination(
        nomination.id,
        { status: result.nomination.status },
        user?.email || 'unknown',
        user?.name || 'Unknown'
      );
      const { adminEmails } = await getReviewerAndAdminEmails();
      notifyNominationApproved(nomination.nominatorId, adminEmails, user?.name || 'Reviewer', nomination.nomineeName, nomination.awardType, nomination.id);
      emailNominationApproved(nomination.nominatorId, adminEmails, user?.name || 'Reviewer', nomination.nomineeName, nomination.awardType, nomination.id);
      refresh();
      setNomination(result.nomination);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) return;
    const result = rejectNomination(nomination, rejectionReason.trim());
    if (result.success && result.nomination) {
      await NominationServiceWrapper.updateNomination(
        nomination.id,
        { status: result.nomination.status, rejectionReason: rejectionReason.trim() },
        user?.email || 'unknown',
        user?.name || 'Unknown'
      );
      notifyNominationRejected(nomination.nominatorId, user?.name || 'Reviewer', nomination.nomineeName, nomination.id, rejectionReason.trim());
      emailNominationRejected(nomination.nominatorId, user?.name || 'Reviewer', nomination.nomineeName, nomination.id, rejectionReason.trim());
      refresh();
      setNomination(result.nomination);
      setShowRejectModal(false);
      setRejectionReason('');
    }
  };

  const handlePublish = async () => {
    const result = publishNomination(nomination, user?.email || 'unknown');
    if (result.success && result.nomination) {
      await NominationServiceWrapper.updateNomination(
        nomination.id,
        {
          status: result.nomination.status,
          publishedAt: result.nomination.publishedAt,
          publishedBy: result.nomination.publishedBy,
        },
        user?.email || 'unknown',
        user?.name || 'Unknown'
      );
      const allEmails = await getAllUserEmails();
      notifyNominationPublished(allEmails, nomination.nomineeName, nomination.awardType, user?.name || 'Admin', nomination.nomineeId);
      emailNominationPublished(allEmails, nomination.nomineeName, nomination.awardType, user?.name || 'Admin', nomination.nomineeId);
      refresh();
      setNomination(result.nomination);
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim()) return;
    const result = await NominationServiceWrapper.addReviewerNote(
      { nominationId: nomination.id, content: noteContent.trim() },
      user?.email || 'unknown',
      user?.name || 'Unknown'
    );
    if (result.success) {
      // Refresh nomination to get updated notes
      const refreshed = await NominationServiceWrapper.getNomination(nomination.id);
      if (refreshed) setNomination(refreshed);
      setNoteContent('');
    }
  };

  return (
    <div className="nomination-detail">
      <div className="nomination-detail-header">
        <div className="header-left-content">
          <button className="nomination-detail-back-btn" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h2>Nomination Details</h2>
        </div>
      </div>

      <div className="nomination-detail-content">
        {/* Nomination Info */}
        <div className="nomination-detail-section">
          <h3 className="nomination-detail-section-title">Nomination Information</h3>
          <div className="nomination-detail-grid">
            <div className="nomination-detail-field">
              <span className="nomination-detail-label">Nominee/Team</span>
              <span className="nomination-detail-value">{nomination.nomineeName}</span>
            </div>
            {/*
              * The roster, and only for a team.
              *
              * An individual nomination already says who it is on the line above, so a second
              * field repeating that one name would be noise on every nomination ever filed.
              * A team with no stored roster — one saved before the schema was applied, or with a
              * damaged teamMembers value — shows nothing here rather than an empty list.
              */}
            {isTeamNomination(nomination) && (nomination.teamMembers?.length || 0) > 0 && (
              <div className="nomination-detail-field">
                <span className="nomination-detail-label">
                  Team Members ({nomination.teamMembers!.length})
                </span>
                <span className="nomination-detail-value">
                  {nomination.teamMembers!.map(member => member.name || member.id).join(', ')}
                </span>
              </div>
            )}
            <div className="nomination-detail-field">
              <span className="nomination-detail-label">Award Type</span>
              <span className="nomination-detail-value">{nomination.awardType}</span>
            </div>
            <div className="nomination-detail-field">
              <span className="nomination-detail-label">Status</span>
              <span className={`nomination-status-badge ${getStatusBadgeClass(nomination.status)}`}>
                {nomination.status}
              </span>
            </div>
            <div className="nomination-detail-field">
              <span className="nomination-detail-label">Nominated By</span>
              <span className="nomination-detail-value">{nomination.nominatorName}</span>
            </div>
            <div className="nomination-detail-field">
              <span className="nomination-detail-label">Created</span>
              <span className="nomination-detail-value">{formatNominationDate(nomination.createdAt)}</span>
            </div>
            <div className="nomination-detail-field">
              <span className="nomination-detail-label">Last Updated</span>
              <span className="nomination-detail-value">{formatNominationDate(nomination.updatedAt)}</span>
            </div>
            {nomination.publishedAt && (
              <div className="nomination-detail-field">
                <span className="nomination-detail-label">Published</span>
                <span className="nomination-detail-value">{formatNominationDate(nomination.publishedAt)}</span>
              </div>
            )}
            <div className="nomination-detail-field full-width">
              <span className="nomination-detail-label">Reason</span>
              <span className="nomination-detail-value">{cleanReason(nomination.reason)}</span>
            </div>
            {nomination.supportingDetails && (
              <div className="nomination-detail-field full-width">
                <span className="nomination-detail-label">Nomination Details</span>
                {renderFormDetails(nomination)}
              </div>
            )}
          </div>
        </div>

        {/* Reviewer Notes - Requirements: 3.2, 3.5 */}
        <div className="nomination-detail-section">
          <h3 className="nomination-detail-section-title">
            Reviewer Notes ({nomination.reviewerNotes.length})
          </h3>
          {nomination.reviewerNotes.length > 0 ? (
            <div className="nomination-notes-list">
              {nomination.reviewerNotes.map((note) => (
                <div key={note.id} className="nomination-note-item">
                  <div className="nomination-note-header">
                    <span className="nomination-note-reviewer">{note.reviewerName}</span>
                    <span className="nomination-note-date">{formatNominationDate(note.createdAt)}</span>
                  </div>
                  <p className="nomination-note-content">{note.content}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="nomination-notes-empty">No reviewer notes yet.</p>
          )}

          {/* Add note form for reviewers */}
          {canReviewNomination(nominationRole) && isReviewable && (
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <input
                type="text"
                className="nomination-form-input"
                placeholder="Add a note..."
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddNote(); }}
                style={{ flex: 1 }}
              />
              <button
                className="nomination-action-btn edit"
                onClick={handleAddNote}
                disabled={!noteContent.trim()}
              >
                Add Note
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="nomination-detail-actions">
        {/* Manager: edit own draft */}
        {isOwnNomination && isDraft && canCreateNomination(nominationRole) && (
          <button
            className="nomination-action-btn edit"
            onClick={() => navigate(`/nominations/${nomination.id}/edit`)}
          >
            Edit Draft
          </button>
        )}

        {/* Manager: submit own draft */}
        {isOwnNomination && isDraft && canCreateNomination(nominationRole) && (
          <button
            className="nomination-action-btn submit"
            onClick={async () => {
              try {
                const result = await NominationServiceWrapper.updateNomination(
                  nomination.id,
                  { status: NominationStatus.SUBMITTED },
                  user?.email || '',
                  user?.name || ''
                );
                if (result.success && result.nomination) {
                  const { reviewerEmails, adminEmails } = await getReviewerAndAdminEmails();
                  notifyNominationSubmitted(reviewerEmails, adminEmails, user?.name || 'User', nomination.nomineeName, nomination.awardType, nomination.id);
                  emailNominationSubmitted(reviewerEmails, adminEmails, user?.name || 'User', nomination.nomineeName, nomination.awardType, nomination.id);
                  refresh();
                  setNomination(result.nomination);
                }
              } catch (err) {
                console.error('Failed to submit nomination:', err);
              }
            }}
          >
            Submit Nomination
          </button>
        )}

        {/* Manager: delete own draft */}
        {isOwnNomination && isDraft && canCreateNomination(nominationRole) && (
          <button
            className="nomination-action-btn reject"
            onClick={async () => {
              if (!window.confirm('Are you sure you want to delete this draft nomination? This cannot be undone.')) return;
              try {
                const result = await NominationServiceWrapper.deleteNomination(nomination.id, user?.email || '', user?.name || '');
                if (result.success) {
                  navigate('/nominations/my');
                }
              } catch (err) {
                console.error('Failed to delete draft:', err);
              }
            }}
          >
            Delete Draft
          </button>
        )}

        {/* Reviewer: approve */}
        {canReviewNomination(nominationRole) && isReviewable && (
          <button className="nomination-action-btn approve" onClick={handleApprove}>
            Approve
          </button>
        )}

        {/* Reviewer: reject */}
        {canReviewNomination(nominationRole) && isReviewable && (
          <button className="nomination-action-btn reject" onClick={() => setShowRejectModal(true)}>
            Reject
          </button>
        )}

        {/* Reviewer: publish approved */}
        {canPublishNomination(nominationRole) && nomination.status === NominationStatus.APPROVED && (
          <button className="nomination-action-btn publish" onClick={handlePublish}>
            Publish
          </button>
        )}
      </div>

      {/* Rejection Reason Modal */}
      {showRejectModal && (
        <div className="rejection-reason-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="rejection-reason-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Rejection Reason</h3>
            <textarea
              className="rejection-reason-textarea"
              placeholder="Provide a reason for rejection (required)"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
            <div className="rejection-reason-actions">
              <button
                className="nomination-action-btn cancel"
                onClick={() => { setShowRejectModal(false); setRejectionReason(''); }}
              >
                Cancel
              </button>
              <button
                className="nomination-action-btn reject"
                onClick={handleRejectConfirm}
                disabled={!rejectionReason.trim()}
              >
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NominationDetail;
